import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMeasure } from 'react-use';

import clsx from 'clsx';

import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { getMenuItemsOrder } from '@/utils/menu/getMenuItemsOrder';
// Utils
import { updateActiveItem } from '@/utils/menu/updateActiveItem';
// Providers
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';

import type { SelectedProfilesProps } from '@/components/composite/SelectedProfiles';
import { AdditionalProfilesPopover } from '@/components/composite/SelectedProfiles/ProfilesList/AdditionalProfilesPopover';
// Components
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';

type ProfilesListProps = Required<SelectedProfilesProps> & {
    profiles: SocialProfileType[];
};

export function ProfilesList({ profiles, itemsSize }: ProfilesListProps) {
    const [ref, { width }] = useMeasure();

    return (
        <div
            ref={ref}
            className={clsx('relative h-9 flex-1', { 'sm:h-11.5': itemsSize === 'base' })}>
            <ProfilesListContent itemsSize={itemsSize} profiles={profiles} width={width} />
        </div>
    );
}

type ProfilesListContentProps = ProfilesListProps & {
    width: number;
};

type OptionsType = {
    itemWidth: number;
    spacing: number;
};

const calculateMaxItems = (totalWidth: number, nbMaxItems: number, options: OptionsType) => {
    const itemWidth = options.itemWidth;
    const spacing = options.spacing;

    const maxItems = Math.floor(totalWidth / (itemWidth + spacing));

    if (maxItems < nbMaxItems) {
        return maxItems - 2;
    }

    return nbMaxItems;
};

function ProfilesListContent({ profiles, width, itemsSize }: ProfilesListContentProps) {
    const isMobile = itemsSize === 'md' || window.innerWidth < 640;
    const launchCalcMaxItems = useCallback(() => {
        return calculateMaxItems(width, profiles.length, {
            itemWidth: isMobile ? 36 : 46,
            spacing: 16
        });
    }, [isMobile, profiles.length, width]);

    const [nbMaxItemDisplayed, setNbMaxItemDisplayed] = useState(launchCalcMaxItems());

    const { updateActiveSocialProfileId } = useProfilesStore((state) => state);
    const { activeSocialProfile } = useActiveSocialProfile();

    const orderedProfiles = useMemo(() => {
        return getMenuItemsOrder({
            storageKey: 'ico-social-profile-list-order',
            menuItems: profiles,
            activeItemId: activeSocialProfile?.id
        });
    }, [JSON.stringify(profiles), activeSocialProfile?.id]);

    const updateActive = (profile: SocialProfileType) => {
        updateActiveItem<SocialProfileType>({
            storageKey: 'ico-social-profile-list-order',
            itemsList: profiles,
            itemSelectedId: profile.id,
            callback: () => {
                updateActiveSocialProfileId(profile.id);
                const activeElement = document.activeElement as HTMLButtonElement;
                activeElement.blur();
            }
        });
    };

    useEffect(() => {
        setNbMaxItemDisplayed(launchCalcMaxItems());
    }, [profiles.length, width, launchCalcMaxItems]);

    const displayedProfiles = orderedProfiles.slice(0, nbMaxItemDisplayed);
    const additionalProfiles = orderedProfiles.slice(nbMaxItemDisplayed);

    return (
        <div className="absolute left-0 top-0 flex w-full items-center gap-4">
            {activeSocialProfile ? (
                <ProfileItem
                    size={isMobile ? 'md' : 'base'}
                    profile={activeSocialProfile}
                    isActive
                />
            ) : null}
            {displayedProfiles.map((profile) => {
                return (
                    <ProfileItem
                        size={isMobile ? 'md' : 'base'}
                        data-testid="visible-in-menu"
                        key={profile.id}
                        profile={profile}
                        onClick={() => updateActive(profile)}
                    />
                );
            })}
            {additionalProfiles.length ? (
                <AdditionalProfilesPopover
                    profiles={additionalProfiles}
                    itemsSize={itemsSize}
                    updateActive={(profile) => updateActive(profile)}
                />
            ) : null}
        </div>
    );
}
